import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../common/button/commonButton";
import ReusableTable from "../../common/table/CommonTable";
import CustomTextField from "../../common/textfield/CommonTextField";
import { fetchBlocks } from "../../redux/block/blockAction";
import { fetchFloors } from "../../redux/floor/floorAction";
import {
  addWard,
  deleteWard,
  fetchAllWards,
  updateWard,
} from "../../redux/ward/wardAction";

const WardManagementForm = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [wardToDelete, setWardToDelete] = useState(null);

  const prevBlockIdRef = useRef(null);
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm();
  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const wards = useSelector((state) => state?.ward?.fetchAllWards || {});
  const wardsAdd = useSelector((state) => state?.ward?.addWard || {});
  const wardsUpdate = useSelector((state) => state?.ward?.updateWard || {});
  const totalPages = wards?.wards?.totalPages;

  const fetchData = (page) => {
    dispatch(fetchAllWards(page));
    dispatch(fetchBlocks());
  };
  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, dispatch]);

  useEffect(() => {}, [dispatch]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  const watchBlock = watch("block_id");
  useEffect(() => {
    if (watchBlock && watchBlock !== prevBlockIdRef.current) {
      prevBlockIdRef.current = watchBlock;
      dispatch(fetchFloors(watchBlock, currentPage));
    }
  }, [watchBlock, dispatch]);

  useEffect(() => {
    if (openDialog) {
      if (dialogMode === "edit" && selectedBlock) {
        setValue("block_id", selectedBlock.block_id);
        setValue("floor_id", selectedBlock.floor_id);
        setValue("ward_name", selectedBlock.Ward_name);
        setValue("visitor_start_time", selectedBlock.visitor_start_time);
        setValue("visitor_end_time", selectedBlock.visitor_end_time);
        setValue(
          "ward_type",
          selectedBlock.ward_type ? selectedBlock.ward_type.toString() : ""
        );
        if (
          selectedBlock.weekday_id !== null &&
          selectedBlock.weekday_id !== undefined
        ) {
          setValue("weekday_id", selectedBlock.weekday_id.toString());
        } else {
          setValue("weekday_id", "");
        }

        setValue("status", selectedBlock.status ? "1" : "2");
        if (selectedBlock.block_id) {
          dispatch(fetchFloors(selectedBlock.block_id, currentPage));
        }
      } else {
        reset({
          block_id: "",
          floor_id: "",
          ward_name: "",
          visitor_start_time: "",
          visitor_end_time: "",
          ward_type: "",
          weekday_id: "",
          status: "1",
        });
      }
      setApiError(null);
    }
  }, [openDialog, dialogMode, selectedBlock, setValue, reset, dispatch]);
  useEffect(() => {
    if (!wardsAdd.inProgress) {
      if (wardsAdd?.error || wardsAdd?.errorMessage || wardsAdd?.errormessage) {
        setApiError(
          wardsAdd.error ||
            wardsAdd.errorMessage ||
            wardsAdd.errormessage ||
            "An error occurred"
        );
        setIsLoading(false);
      } else if (wardsAdd?.success) {
        setIsLoading(false);
        handleCloseDialog();
        fetchData(currentPage);
        setSnackbarMessage("Ward added successfully");
        setSnackbarOpen(true);
      }
    }
  }, [wardsAdd, dispatch]);

  useEffect(() => {
    if (!wardsUpdate.inProgress) {
      if (
        wardsUpdate?.error ||
        wardsUpdate?.errorMessage ||
        wardsUpdate?.errormessage
      ) {
        setApiError(
          wardsUpdate.error ||
            wardsUpdate.errorMessage ||
            wardsUpdate.errormessage ||
            "An error occurred"
        );
        setIsLoading(false);
      } else if (wardsUpdate?.success) {
        setIsLoading(false);
        handleCloseDialog();
        fetchData(currentPage);
        setSnackbarMessage("Ward updated successfully");
        setSnackbarOpen(true);
      }
    }
  }, [wardsUpdate, dispatch]);

  const columns = [
    { id: "serialNo", label: "Serial No", sortable: true },
    { id: "block_name", label: "Block name", sortable: true },
    { id: "Ward_name", label: "Ward Name", sortable: true },
    { id: "floor_name", label: "Floor Name", sortable: true },
    {
      id: "ward_type",
      label: "Ward Type",
      sortable: true,
      render: (value) => (value === 1 ? "Emergency" : "Casual"),
    },
    {
      id: "day_name",
      label: "Day",
      sortable: true,
      render: (value) => {
        const days = {
          2: "Monday",
          3: "Tuesday",
          4: "Wednesday",
          5: "Thursday",
          6: "Friday",
          7: "Saturday",
          1: "Sunday",
          8: "All Days",
        };
        return days[value] || "N/A";
      },
    },
    {
      id: "status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value ? "Active" : "Inactive"}
          color={value ? "success" : "error"}
          variant="filled"
          size="small"
          sx={{
            borderRadius: "16px",
            fontWeight: "bold",
            minWidth: "80px",
          }}
        />
      ),
    },
  ];

  const handleOpenDialog = (mode, block = null) => {
    setDialogMode(mode);
    setSelectedBlock(block);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setApiError(null);
    reset();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const onSubmit = (data) => {
    setIsLoading(true);
    setApiError(null);

    const wardData = {
      block_id: data.block_id,
      floor_id: data.floor_id,
      ward_name: data.ward_name,
      visitor_start_time: data.visitor_start_time,
      visitor_end_time: data.visitor_end_time,
      ward_type: parseInt(data.ward_type, 10),
      weekday_id: data.weekday_id !== "" ? parseInt(data.weekday_id, 10) : null,
      ward_status: data.status === "1" ? 1 : 0,
    };

    try {
      if (dialogMode === "add") {
        dispatch(addWard(wardData));
      } else {
        const updateData = {
          ...wardData,
          id: selectedBlock.id,
        };
        dispatch(updateWard(updateData));
      }
    } catch (error) {
      console.error("Error with ward operation:", error);
      setIsLoading(false);
      setApiError("An unexpected error occurred");
    }
  };

  const handleOpenDeleteConfirm = (ward) => {
    setWardToDelete(ward);
    setDeleteConfirmDialog(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmDialog(false);
    setWardToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (wardToDelete) {
      dispatch(deleteWard(wardToDelete.id));

      setTimeout(() => {
        fetchData(currentPage);
        setSnackbarMessage("Ward deleted successfully");
        setSnackbarOpen(true);
      }, 1000);

      handleCloseDeleteConfirm();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Ward Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog("add")}
          sx={{
            borderRadius: "20px",
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: "bold",
            color: "white",
            bgcolor: "#2c90f7",
          }}
        >
          Add New Ward
        </Button>
      </Box>

      <ReusableTable
        columns={columns}
        data={
          wards?.wards?.data?.map((ward, index) => ({
            id: ward.id,
            serialNo: index + 1,
            block_name: ward.Block?.block_name || "N/A",
            Ward_name: ward.ward_name,
            floor_name: ward.Floor?.floor_name || "N/A",
            ward_type: ward.ward_type,
            status: ward.ward_status === 1,
            block_id: ward.block_id,
            floor_id: ward.floor_id,
            visitor_start_time: ward.visitor_start_time,
            visitor_end_time: ward.visitor_end_time,
            weekday_id: ward.weekday_id,
            day_name: ward.weekday_id,
          })) || []
        }
        isLoading={wards.inProgress}
        title="Wards"
        onEditClick={(ward) => handleOpenDialog("edit", ward)}
        onDeleteClick={(ward) => handleOpenDeleteConfirm(ward)}
        customEmptyMessage="No wards found. Add a new ward to get started."
        headerColor="#000000"
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        withActions={true}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            bgcolor: "#129bab",
            color: "white",
          }}
        >
          {dialogMode === "add" ? "Add Ward" : "Edit Ward"}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            {apiError && (
              <Box
                sx={{
                  bgcolor: "#fff4f4",
                  p: 2,
                  borderRadius: 1,
                  mb: 2,
                  border: "1px solid #f44336",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography color="error">{apiError}</Typography>
                <IconButton size="small" onClick={() => setApiError(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Block Name*
                    </Typography>
                    <Select
                      {...register("block_id", {
                        required: "Block Name is required",
                      })}
                      value={watch("block_id", "")}
                      onChange={(e) => setValue("block_id", e.target.value)}
                      fullWidth
                      error={!!errors.block_id}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Block
                      </MenuItem>
                      {blockList?.Blocks?.data
                        ?.filter((block) => block.block_status !== 2)
                        .map((block) => (
                          <MenuItem key={block.id} value={block.id}>
                            {block.block_name}
                          </MenuItem>
                        )) || []}
                    </Select>
                    {errors.block_id && (
                      <Typography color="error" variant="caption">
                        {errors.block_id.message}
                      </Typography>
                    )}
                  </Box>

                  {/* Floor Name */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Floor Name*
                    </Typography>
                    <Select
                      {...register("floor_id", {
                        required: "Floor Name is required",
                      })}
                      value={watch("floor_id", "")}
                      onChange={(e) => setValue("floor_id", e.target.value)}
                      fullWidth
                      error={!!errors.floor_id}
                      displayEmpty
                      disabled={!watchBlock}
                    >
                      <MenuItem value="" disabled>
                        Select Floor
                      </MenuItem>
                      {floorList?.floors?.data
                        ?.filter((floor) => floor.floor_status !== 2)
                        .map((floor) => (
                          <MenuItem key={floor.id} value={floor.id}>
                            {floor.floor_name}
                          </MenuItem>
                        )) || []}
                    </Select>
                    {errors.floor_id && (
                      <Typography color="error" variant="caption">
                        {errors.floor_id.message}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <CustomTextField
                      label="Ward Name*"
                      {...register("ward_name", {
                        required: "Ward Name is required",
                      })}
                      value={watch("ward_name", "")}
                      onChange={(e) => setValue("ward_name", e.target.value)}
                      error={!!errors.ward_name}
                      helperText={errors.ward_name?.message}
                      fullWidth
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Ward Type*
                    </Typography>
                    <Select
                      {...register("ward_type", {
                        required: "Ward Type is required",
                      })}
                      value={watch("ward_type", "")}
                      onChange={(e) => setValue("ward_type", e.target.value)}
                      fullWidth
                      error={!!errors.ward_type}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Ward Type
                      </MenuItem>
                      <MenuItem value="1">Emergency</MenuItem>
                      <MenuItem value="2">Casual</MenuItem>
                    </Select>
                    {errors.ward_type && (
                      <Typography color="error" variant="caption">
                        {errors.ward_type.message}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <CustomTextField
                      label="Visitor Start Time*"
                      {...register("visitor_start_time", {
                        required: "Visitor Start Time is required",
                      })}
                      value={watch("visitor_start_time", "")}
                      onChange={(e) =>
                        setValue("visitor_start_time", e.target.value)
                      }
                      error={!!errors.visitor_start_time}
                      helperText={errors.visitor_start_time?.message}
                      fullWidth
                      type="time"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />
                  </Box>
                  <Box sx={{ mb: 3 }}>
                    <CustomTextField
                      label="Visitor End Time*"
                      {...register("visitor_end_time", {
                        required: "Visitor End Time is required",
                      })}
                      value={watch("visitor_end_time", "")}
                      onChange={(e) =>
                        setValue("visitor_end_time", e.target.value)
                      }
                      error={!!errors.visitor_end_time}
                      helperText={errors.visitor_end_time?.message}
                      fullWidth
                      type="time"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />
                  </Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Day of Week*
                    </Typography>
                    <Select
                      {...register("weekday_id", {
                        required: "Day of Week is required",
                      })}
                      value={watch("weekday_id", "")}
                      onChange={(e) => setValue("weekday_id", e.target.value)}
                      fullWidth
                      error={!!errors.weekday_id}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Day
                      </MenuItem>
                      <MenuItem value="1">Sunday</MenuItem>
                      <MenuItem value="2">Monday</MenuItem>
                      <MenuItem value="3">Tuesday</MenuItem>
                      <MenuItem value="4">Wednesday</MenuItem>
                      <MenuItem value="5">Thursday</MenuItem>
                      <MenuItem value="6">Friday</MenuItem>
                      <MenuItem value="7">Saturday</MenuItem>
                      <MenuItem value="8">All Days</MenuItem>
                    </Select>
                    {errors.weekday_id && (
                      <Typography color="error" variant="caption">
                        {errors.weekday_id.message}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Status
                    </Typography>
                    <Select
                      {...register("status")}
                      value={watch("status", "1")}
                      onChange={(e) => setValue("status", e.target.value)}
                      fullWidth
                    >
                      <MenuItem value="1">Active</MenuItem>
                      <MenuItem value="2">Inactive</MenuItem>
                    </Select>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              bgcolor: "#129bab",
              justifyContent: "center",
            }}
          >
            <CustomButton
              onClick={handleCloseDialog}
              type="button"
              text="Cancel"
              variant="contained"
              disableElevation
              size="small"
            >
              Cancel
            </CustomButton>
            <CustomButton
              type="submit"
              text={dialogMode === "add" ? "Add" : "Update"}
              variant="contained"
              disableElevation
              size="small"
              startIcon={<AddIcon />}
              disabled={wardsAdd?.inProgress || wardsUpdate?.inProgress}
              loading={wardsAdd?.inProgress || wardsUpdate?.inProgress}
            />
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={deleteConfirmDialog}
        onClose={handleCloseDeleteConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            bgcolor: "#f44336",
            color: "white",
          }}
        >
          Confirm Delete
          <IconButton
            aria-label="close"
            onClick={handleCloseDeleteConfirm}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, p: 3 }}>
          <Typography variant="body1">
            Are you sure you want to delete{" "}
            {wardToDelete?.Ward_name || "this ward"}? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: "center" }}>
          <CustomButton
            onClick={handleCloseDeleteConfirm}
            type="button"
            text="Cancel"
            variant="contained"
            disableElevation
            size="small"
            sx={{ bgcolor: "#e0e0e0", color: "#000000" }}
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handleConfirmDelete}
            type="button"
            text="Delete"
            variant="contained"
            disableElevation
            size="small"
            sx={{ bgcolor: "#f44336" }}
          >
            Delete
          </CustomButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WardManagementForm;
